/**
 * Send one image straight to the browser's own print dialog.
 *
 * A hidden iframe rather than `window.open`: the print pass runs after an async
 * full-resolution render, so a popup opened at that point is outside the user's
 * click gesture and blockers eat it. The iframe has no such problem and never
 * flashes a second tab.
 *
 * Paper size, orientation and margins are deliberately NOT set here — the print
 * dialog already asks for them, and `@page { size: auto }` lets whatever the user
 * picks there win. The sheet just fits the page inside it.
 *
 * TIMING, the whole reason this file is careful: an iframe fires `load` TWICE —
 * once for the initial about:blank, then again after `document.write`. Printing
 * on `load` therefore opened the dialog on an empty document, and only the second
 * (cancelled) pass showed the image. So the print is driven off the IMAGE being
 * decoded instead, and a `printed` latch makes sure the dialog opens exactly once.
 */

/**
 * The print document. Exported so its markup can be asserted without a dialog.
 *
 * Takes a LIST: a cleanup job is a stack of pages, and printing them one dialog
 * at a time is the thing the page rail exists to avoid. Each page is its own
 * full-height flex sheet with a page break after it, so one dialog prints the
 * whole set, one page per sheet.
 */
export function buildPrintDocument(dataUrls, title = 'Print') {
    const list = [].concat(dataUrls || []).filter(Boolean);
    const sheets = list
        .map((src) => `<div class="sheet"><img src="${src}" alt="" /></div>`)
        .join('');
    return `<!doctype html><html><head><meta charset="utf-8"><title>${title}</title>
<style>
  @page { size: auto; margin: 10mm; }
  html, body { height: 100%; margin: 0; padding: 0; background: #fff; }
  body {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .sheet {
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    break-inside: avoid;
    page-break-inside: avoid;
  }
  /* No break after the LAST sheet, or every job prints a blank final page. */
  .sheet + .sheet { break-before: page; page-break-before: always; }
  img { max-width: 100%; max-height: 100%; object-fit: contain; }
</style></head><body>${sheets}</body></html>`;
}

/** Give up waiting on the decode and print anyway. */
const DECODE_TIMEOUT_MS = 5000;

/** One data URL, or a whole stack of them — one dialog either way. */
export function printImageDataUrl(dataUrl, title = 'Print') {
    return new Promise((resolve) => {
        const urls = [].concat(dataUrl || []).filter(Boolean);
        if (!urls.length) {
            resolve(false);
            return;
        }

        const frame = document.createElement('iframe');
        frame.setAttribute('aria-hidden', 'true');
        // Offscreen but a REAL size: a 0×0 or visibility:hidden frame gets its
        // layout (and rAF) throttled, which is another way to end up printing a
        // blank sheet. A4 at 96dpi keeps the layout honest.
        frame.style.cssText =
            'position:fixed;left:-10000px;top:0;width:794px;height:1123px;border:0';

        let removed = false;
        // Removing the iframe too early cancels the print job, so teardown waits for
        // the dialog — but the PROMISE does not, or a browser that never fires
        // `onafterprint` would leave the caller's busy state stuck on the fallback.
        const cleanup = () => {
            if (removed) return;
            removed = true;
            frame.remove();
        };

        document.body.appendChild(frame);

        const doc = frame.contentDocument;
        const win = frame.contentWindow;
        if (!doc || !win) {
            cleanup();
            resolve(false);
            return;
        }

        doc.open();
        doc.write(buildPrintDocument(urls, title));
        doc.close();

        let printed = false;
        const runPrint = () => {
            if (printed || removed) return;
            printed = true;

            win.onafterprint = () => setTimeout(cleanup, 300);
            // Safari fires onafterprint unreliably — don't leak the iframe.
            setTimeout(cleanup, 60000);

            try {
                win.focus();
                win.print();
            } catch {
                cleanup();
                resolve(false);
                return;
            }
            resolve(true);
        };

        const images = [...doc.querySelectorAll('img')];
        if (!images.length) {
            runPrint();
            return;
        }

        // Every sheet must be laid out before the dialog opens, or the pages
        // still decoding print blank. A page that fails to decode is counted as
        // settled rather than aborting the job: nineteen good sheets beat none.
        let pending = images.length;
        const settle = () => {
            pending -= 1;
            // One tick so the frame has laid the last image out before the
            // print preview snapshots it.
            if (pending <= 0) setTimeout(runPrint, 0);
        };
        images.forEach((img) => {
            if (img.complete && img.naturalWidth) { settle(); return; }
            img.addEventListener('load', settle, { once: true });
            img.addEventListener('error', settle, { once: true });
        });

        setTimeout(runPrint, DECODE_TIMEOUT_MS);
    });
}
